import { callColorCumulative, putColorCumulative } from '../../components/multiStikeChart/styles'
import { ExpiryOpenInterestProto } from '../../proto/generated/open-interest'
import { ParticipantChartMetaInfo } from './OIChart'

export type OiChartRow = { date: string } & Record<string, number | string>

export const OI_CHART_META_INFO: ParticipantChartMetaInfo = {
    colors: new Map([
        ['Call OI', callColorCumulative],
        ['Put OI', putColorCumulative],
    ]),
    rightAxisLabel: 'Call/Put Qty',
}

/** Strike list visible on the OI chart (same rules as chart strike filter). */
export function filterStrikesAroundLtp(strikes: number[], strikeCount: string, stableLastTradedPrice: number): number[] {
    if (strikeCount === 'all') {
        return strikes
    }

    const count = parseInt(strikeCount, 10)
    const sortedStrikes = [...strikes].sort((a, b) => a - b)

    if (!stableLastTradedPrice) {
        const middleIndex = Math.floor(sortedStrikes.length / 2)
        const startIndex = Math.max(0, middleIndex - Math.floor(count / 2))
        return sortedStrikes.slice(startIndex, startIndex + count)
    }

    let closestIndex = 0
    let minDiff = Math.abs(sortedStrikes[0] - stableLastTradedPrice)

    for (let i = 1; i < sortedStrikes.length; i++) {
        const diff = Math.abs(sortedStrikes[i] - stableLastTradedPrice)
        if (diff < minDiff) {
            minDiff = diff
            closestIndex = i
        }
    }

    const strikesOnEachSide = Math.floor(count / 2)

    let startIndex = closestIndex - strikesOnEachSide
    let endIndex = closestIndex + strikesOnEachSide + (count % 2)

    if (startIndex < 0) {
        endIndex += Math.abs(startIndex)
        startIndex = 0
    }
    if (endIndex > sortedStrikes.length) {
        startIndex -= endIndex - sortedStrikes.length
        endIndex = sortedStrikes.length
        startIndex = Math.max(0, startIndex)
    }

    return sortedStrikes.slice(startIndex, endIndex)
}

export function computeOiChartDataSignature(data: OiChartRow[]): string {
    try {
        return JSON.stringify(
            data.map((row) => {
                const sorted: Record<string, unknown> = {}
                for (const k of Object.keys(row).sort()) {
                    sorted[k] = row[k]
                }
                return sorted
            })
        )
    } catch {
        return `len:${data?.length ?? 0}`
    }
}

export function oiChartDataEqual(prev: OiChartRow[], next: OiChartRow[]): boolean {
    if (prev === next) return true
    return computeOiChartDataSignature(prev) === computeOiChartDataSignature(next)
}

type BuildParams = {
    oiChangeData: ExpiryOpenInterestProto[]
    selectedExpiries: string[]
    strikeCount: string
    strikeFilterLtp: number
    priceMultiplier: number
    showOI: boolean
    isOIChangeScreen: boolean
}

export function buildOiChartData({
    oiChangeData,
    selectedExpiries,
    strikeCount,
    strikeFilterLtp,
    priceMultiplier,
    showOI,
    isOIChangeScreen,
}: BuildParams): OiChartRow[] {
    const filteredOiChangeData =
        selectedExpiries.length > 0
            ? oiChangeData.filter((expiryData) => selectedExpiries.includes(expiryData.expiry.toString()))
            : oiChangeData

    if (showOI) {
        const strikeToOiData: Record<
            number,
            {
                strikePrice: number
                callOiFrom: number
                putOiFrom: number
                callOiTo: number
                putOiTo: number
            }
        > = {}

        filteredOiChangeData.forEach((expiryWiseData) => {
            expiryWiseData.strikeOptionChainLineItem.forEach((strikeData) => {
                const fromCallOi = strikeData?.from?.callOi ?? 0
                const fromPutOi = strikeData?.from?.putOi ?? 0
                const toCallOi = strikeData?.to?.callOi ?? 0
                const toPutOi = strikeData?.to?.putOi ?? 0

                strikeToOiData[strikeData.strikePrice] = {
                    strikePrice: strikeData.strikePrice,
                    callOiFrom: (strikeToOiData?.[strikeData.strikePrice]?.callOiFrom ?? 0) + fromCallOi,
                    putOiFrom: (strikeToOiData?.[strikeData.strikePrice]?.putOiFrom ?? 0) + fromPutOi,
                    callOiTo: (strikeToOiData?.[strikeData.strikePrice]?.callOiTo ?? 0) + toCallOi,
                    putOiTo: (strikeToOiData?.[strikeData.strikePrice]?.putOiTo ?? 0) + toPutOi,
                }
            })
        })

        const allStrikes = Object.keys(strikeToOiData).map(Number)
        const filteredStrikes = filterStrikesAroundLtp(allStrikes, strikeCount ?? '25', strikeFilterLtp)
        const filteredData = Object.values(strikeToOiData).filter((d) => filteredStrikes.includes(d.strikePrice))

        return filteredData.map((d) => ({
            date: (d.strikePrice / priceMultiplier).toString(),
            'Call OI From': d.callOiFrom,
            'Put OI From': d.putOiFrom,
            'Call OI To': d.callOiTo,
            'Put OI To': d.putOiTo,
        }))
    }

    const strikeToOiData: Record<number, { strikePrice: number; callOi: number; putOi: number }> = {}

    filteredOiChangeData.forEach((expiryWiseData) => {
        expiryWiseData.strikeOptionChainLineItem.forEach((strikeData) => {
            if (isOIChangeScreen) {
                strikeToOiData[strikeData.strikePrice] = {
                    strikePrice: strikeData.strikePrice,
                    callOi:
                        (strikeToOiData?.[strikeData.strikePrice]?.callOi ?? 0) +
                        ((strikeData?.to?.callOi ?? 0) - (strikeData?.from?.callOi ?? 0)),
                    putOi:
                        (strikeToOiData?.[strikeData.strikePrice]?.putOi ?? 0) +
                        ((strikeData?.to?.putOi ?? 0) - (strikeData?.from?.putOi ?? 0)),
                }
            } else {
                const toCallOi = strikeData?.to?.callOi ?? 0
                const toPutOi = strikeData?.to?.putOi ?? 0
                strikeToOiData[strikeData.strikePrice] = {
                    strikePrice: strikeData.strikePrice,
                    callOi: (strikeToOiData?.[strikeData.strikePrice]?.callOi ?? 0) + toCallOi,
                    putOi: (strikeToOiData?.[strikeData.strikePrice]?.putOi ?? 0) + toPutOi,
                }
            }
        })
    })

    const allStrikes = Object.keys(strikeToOiData).map(Number)
    const filteredStrikes = filterStrikesAroundLtp(allStrikes, strikeCount ?? '25', strikeFilterLtp)
    const filteredData = Object.values(strikeToOiData).filter((d) => filteredStrikes.includes(d.strikePrice))

    return filteredData.map((d) => ({
        date: (d.strikePrice / priceMultiplier).toString(),
        'Call OI': d.callOi,
        'Put OI': d.putOi,
    }))
}

/** Closest visible strike to LTP (ATM). `strikeFilterLtp` is in the same units as proto strikePrice. */
export function findAtmOiChartRow(
    rows: OiChartRow[],
    strikeFilterLtp: number,
    priceMultiplier: number
): OiChartRow | null {
    if (!rows.length) return null
    if (!strikeFilterLtp) return rows[Math.floor(rows.length / 2)] ?? null
    const target = strikeFilterLtp / (priceMultiplier || 1)
    let best = rows[0]
    let bestDiff = Math.abs(Number(rows[0].date) - target)
    for (let i = 1; i < rows.length; i++) {
        const diff = Math.abs(Number(rows[i].date) - target)
        if (diff < bestDiff) {
            bestDiff = diff
            best = rows[i]
        }
    }
    return best
}
